import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_400, BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUICheckCard } from 'components/common/MUICards'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import type { ProductCode, SalesforceId } from 'models/purchaseFlowRevamp'
import { formatPrice } from 'utils/price/priceFormatter'

import { usePfStore } from '../../store'
import { ExtraServicePicker } from './ExtraServicePicker.component'
import { OptionPicker } from './OptionPicker.component'
import type { PackMemberQuote } from './packMemberQuote'

interface Props {
  quotes: PackMemberQuote[]
  unselectedCodes: readonly ProductCode[]
  onToggle: (productCode: ProductCode) => void
}

/**
 * A pack's services inside the product drawer, as the PoC's bundle panel draws them: one card per service
 * with its description and price, ticked by default. A ticked card reveals the product it sells and the
 * options of that product, such as blurring on photos. Only the ticked services become cart lines.
 */
export const PackMembersSection: React.FC<Props> = ({ quotes, unselectedCodes, onToggle }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  if (quotes.length === 0) return null

  return (
    <Stack gap={0.8} data-cy="pf-revamp-pack-members">

      <Typography variant="text-md" fontWeight={450} color={GRAY_700}>
        {t('purchase_flow_revamp:drawer.bundle_contents_label')}
      </Typography>

      <Stack gap={0.8}>
        {quotes.map((quote) => (
          <PackServiceCard
            key={quote.member.productCode}
            quote={quote}
            checked={!unselectedCodes.includes(quote.member.productCode)}
            onToggle={() => onToggle(quote.member.productCode)}
          />
        ))}
      </Stack>

    </Stack>
  )
}

interface CardProps {
  quote: PackMemberQuote
  checked: boolean
  onToggle: () => void
}

const PackServiceCard: React.FC<CardProps> = ({ quote, checked, onToggle }) => {
  const code = quote.member.productCode
  const addons = usePfStore((s) => s.packMemberAddons[code])
  const setOption = usePfStore((s) => s.setPackMemberOption)
  const clearOption = usePfStore((s) => s.clearPackMemberOption)
  const toggleExtra = usePfStore((s) => s.togglePackMemberExtra)
  const setExtraQuantity = usePfStore((s) => s.setPackMemberExtraQuantity)

  const price = (amount: PackMemberQuote['total']) => amount && quote.currency ? formatPrice(amount, quote.currency) : null
  const hasAddons = quote.options.length > 0 || quote.extras.length > 0

  return (
    <MUICheckCard
      data-cy={`pf-revamp-pack-member-${code}`}
      checked={checked}
      onCheck={onToggle}
      title={quote.shell.displayName}
      description={quote.shell.description}
      trailing={
        <Typography variant="text-md" fontWeight={600} color={GRAY_900} sx={{ whiteSpace: 'nowrap' }}>
          {price(quote.total)}
        </Typography>
      }
      expandableContent={
        <Stack gap={1.6}>

          <SelectableButtonsGrid<SalesforceId>
            layout="vertical"
            readOnly
            gap={0.8}
            selectedValue={code}
            onSelect={() => undefined}
            values={[{
              value: code,
              displayName: (
                <Stack alignItems="center" gap={0.2} sx={{ lineHeight: '2rem' }}>
                  <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>{quote.variant.displayName}</Typography>
                  <Typography variant="text-sm" component="span" color={GRAY_700}>{price(quote.unitPrice)}</Typography>
                </Stack>
              ),
            }]}
            sx={{ flex: '0 0 calc(50% - 0.4rem)', minHeight: '6.4rem', height: 'auto', padding: '1.2rem' }}
          />

          {hasAddons &&
            <Box sx={{ mx: -1.6, mb: -1.6, px: 1.6, py: 1.2, backgroundColor: BEIGE_400, borderTop: `1px solid ${BEIGE_600}`, borderRadius: '0 0 0.7rem 0.7rem' }}>
              <OptionPicker
                options={quote.options}
                values={addons?.optionValues ?? {}}
                onSetValue={(optionId, value) => setOption(code, optionId, value)}
                onClearValue={(optionId) => clearOption(code, optionId)}
                showHeading={false}
              />
              <ExtraServicePicker
                extras={quote.extras}
                selectedExtraIds={addons?.extraIds ?? []}
                extraQuantities={addons?.extraQuantities ?? {}}
                shellProductId={quote.shell.id}
                onToggle={(extraId) => toggleExtra(code, extraId)}
                onSetQuantity={(extraId, quantity) => setExtraQuantity(code, extraId, quantity)}
              />
            </Box>
          }

        </Stack>
      }
    />
  )
}
